import React from 'react';
import { Tooltip } from '@mui/material';
import { CurrenciesModel } from '@models/profile.model.ts';
import { ServiceTypeEnum } from '@models/services.model.ts';
import { formatDate, formatTime } from '@utils/date-format-utils.ts';
import { SelectOption } from '@models/global.model.ts';
import AppImage from '@components/app-image/app-image.tsx';
import {
  ServiceStatusMap,
  serviceTypeOptions
} from '@modules/services/utils/services.utils.ts';
import {
  IsPublishedStatusMap,
  isPublishedStatusOptions
} from '@utils/status-options.utils.ts';
import RowActions from '@components/row-actions/row-actions.tsx';
import Checkbox from '@mui/material/Checkbox';

export const selectAllTableColumns = (
  handleEdit: (row: any) => void,
  handleDelete: (row: any) => void,
  handleSelectRow: (rowId: string) => void,
  handleSelectAll: () => void,
  selectedRows: string[],
  selectedCurrency: CurrenciesModel | null,
  language: string,
  departmentUnit: SelectOption[] | null,
  t: (key: string) => string,
  tableData: any[]
) => [
  {
    id: 'select',
    label: (
      <Checkbox
        className="primary-checkbox"
        checked={
          tableData.length > 0 && selectedRows.length === tableData.length
        }
        indeterminate={
          selectedRows.length > 0 && selectedRows.length < tableData.length
        }
        onChange={handleSelectAll}
      />
    ),
    field: 'select',
    render: (value: any, row: any) => (
      <Checkbox
        className="primary-checkbox"
        checked={selectedRows.includes(row.id)}
        onChange={() => handleSelectRow(row.id)}
      />
    ),
    width: 100,
    showSort: false,
    showSearch: false,
    showFilter: false,
    disabledDrag: true
  },
  {
    id: 'image',
    label: 'column_label_image',
    field: 'image',
    render: (value: any) => (
      <div
        className={`h-13 w-13 rounded-md border border-(--gray-7) overflow-hidden cursor-pointer`}
      >
        <AppImage imgClassName="!h-full" filePath={value ?? undefined} />
      </div>
    ),
    showSort: false,
    showSearch: false,
    showFilter: false
  },
  {
    id: 'name',
    label: 'column_label_name',
    field: 'name',
    render: (value: any) => (
      <Tooltip title={value?.[language] || 'N/A'} followCursor>
        <span>{value?.[language] || 'N/A'} </span>
      </Tooltip>
    ),
    showSort: false,
    showSearch: true,
    showFilter: false,
    isTranslated: true
  },
  {
    id: 'description',
    label: 'column_label_description',
    field: 'description',
    render: (value: any) => (
      <Tooltip title={value?.[language] || 'N/A'} followCursor>
        <span>{value?.[language] || 'N/A'} </span>
      </Tooltip>
    ),
    showSort: false,
    showSearch: true,
    showFilter: false
  },
  {
    id: 'duration',
    label: 'column_label_duration',
    field: 'duration',
    render: (value: any) => {
      let display = 'N/A';

      if (typeof value === 'string') {
        const match = value.match(/^(\d+)([hm])$/i);
        if (match) {
          const num = match[1];
          const unit = match[2].toLowerCase();
          display = `${num} ${unit === 'h' ? 'hours' : 'minutes'}`;
        } else {
          display = value;
        }
      }

      return (
        <Tooltip title={display} followCursor>
          <span>{display}</span>
        </Tooltip>
      );
    },
    showSort: false,
    showSearch: true,
    showFilter: false,
    isTranslated: true
  },
  {
    id: 'price',
    label: 'column_label_price',
    field: 'price',
    render: (value: any) => (
      <Tooltip title={`${value} ${selectedCurrency?.symbol}`} followCursor>
        <span>
          {value ? `${value} ${selectedCurrency?.symbol || ''}` : '-'}
        </span>
      </Tooltip>
    ),
    showSort: true,
    showSearch: true,
    showFilter: false
  },
  {
    id: 'type',
    label: 'column_label_type',
    field: 'type',
    render: (value: ServiceTypeEnum) => {
      const status = ServiceStatusMap[value];

      return (
        <Tooltip title={t(status?.text || '') || value} followCursor>
          <div
            style={{
              background: status?.background || 'inherit',
              color: status?.color || 'inherit',
              fontWeight: 600,
              textAlign: 'center',
              padding: '6px 12px',
              borderRadius: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            {t(status?.text || '') || value || 'N/A'}
          </div>
        </Tooltip>
      );
    },
    showSort: false,
    showSearch: false,
    showFilter: true,
    filterData: serviceTypeOptions,
    showSearchInput: false
  },
  {
    id: 'isPublished',
    label: 'column_label_status',
    field: 'isPublished',
    render: (value: boolean) => {
      const status = IsPublishedStatusMap[String(value) as 'true' | 'false'];

      return (
        <Tooltip
          title={t(status?.text) || (value ? 'Published' : 'Unpublished')}
          followCursor
        >
          <div
            style={{
              background: status?.background || 'inherit',
              color: status?.color || 'inherit',
              fontWeight: 600,
              textAlign: 'center',
              padding: '6px 12px',
              borderRadius: '6px'
            }}
          >
            {t(status?.text) || (value ? 'Published' : 'Unpublished')}
          </div>
        </Tooltip>
      );
    },
    showSort: false,
    showSearch: false,
    showFilter: true,
    filterData: isPublishedStatusOptions,
    showSearchInput: false
  },
  {
    id: 'departmentUnit.name',
    label: 'column_label_department_unit',
    field: 'departmentUnit',
    render: (value: any) => (
      <Tooltip title={value?.name || 'N/A'} followCursor>
        <span>{value?.name || 'N/A'} </span>
      </Tooltip>
    ),
    showSort: false,
    showSearch: false,
    showFilter: true,
    filterData: departmentUnit
  },
  // {
  //   id: 'departmentUnit',
  //   label: 'column_label_working_time',
  //   field: 'departmentUnit',
  //   render: (value: any) => {
  //     const hours = value?.availableHours;

  //     const display =
  //       Array.isArray(hours) && hours.length > 0
  //         ? hours
  //             .map(
  //               ({ open, close }) =>
  //                 `${formatTime(open)} – ${formatTime(close)}`
  //             )
  //             .join(', ')
  //         : '-';

  //     return (
  //       <Tooltip title={display} followCursor>
  //         <span>{display}</span>
  //       </Tooltip>
  //     );
  //   },
  //   showSort: true,
  //   showSearch: true,
  //   showFilter: false,
  //   filterData: false,
  //   showDateRangeFilter: false
  // },
  {
    id: 'createdAt',
    label: 'column_label_createdAt',
    field: 'createdAt',
    render: (value: any) => (
      <Tooltip title={value ? formatDate(value) : '-'} followCursor>
        <span>{value ? formatDate(value) : '-'}</span>
      </Tooltip>
    ),
    showSort: true,
    showSearch: true,
    showFilter: false
  },
  {
    id: 'Action',
    label: 'column_label_actions',
    field: 'actions',
    width: 110,
    render: (value: any, row: any) => (
      <RowActions
        row={row}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />
    ),
    showSort: false,
    showSearch: false,
    showFilter: false
  }
];
